import { Button, Link, TockLogo, Typography } from 'omakase';
import { InView } from 'react-intersection-observer';
import { MarketingWidget as MarketingWidgetType, Modifiers } from '@tock/shared/types/widget';
import Anchor from 'components/Anchor';
import _ from 'lodash';

import {
  ACTION_DOWNLOAD_IOS_APP,
  ACTION_NAVIGATE_TO_BLOG,
  ACTION_NAVIGATE_TO_JOIN,
} from 'utils/analytics/eventTypeConstantsAmplitude';
import { IconArrowRight, IconExternalLink } from '@tock/icons';
import { MarketingWidgetStyles } from './MarketingWidgetCssDefs';
import { Theme, css } from '@emotion/react';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import { useLocation } from 'react-router-dom';
import WidgetLayoutContainer from 'consumer-page-widgets/WidgetUtils/WidgetLayoutContainer';

type Props = {
  widget: MarketingWidgetType;
  pageIndex: number;
};

const MarketingWidget = ({ widget, pageIndex }: Props) => {
  const renderWidget = (
    widgetType: string,
    { pageIndex }: { pageIndex: number },
    search: string
  ) => {
    const baseUrl = 'https://resy.com/join/get-started/';
    const formattedSearch = search.startsWith('?') ? `&${search.slice(1)}` : search;
    const href = `${baseUrl}${formattedSearch}`;
    switch (widgetType) {
      case 'b2b': {
        return (
          <InView threshold={0} rootMargin="200px 0px 200px 0px" triggerOnce>
            {({ inView, ref, entry }) => (
              <div
                ref={ref}
                className="MarketingWidget-messaging"
                css={({ borderRadius }: Theme) => css`
                  background-color: #f4f4f4;
                  border-radius: ${borderRadius[200]};
                `}
              >
                <div
                  className="MarketingWidget-hero"
                  css={css`
                    padding: 1.6rem;
                    @media screen and (min-width: 576px) {
                      display: flex;
                      gap: 52px;
                      justify-content: space-between;
                      align-items: center;
                      padding: 3rem 2rem;
                      > div {
                        flex: 0 0 calc(50% - 26px);
                      }
                      @media screen and (min-width: 1024px) {
                        padding: 5.2rem 5.2rem 5.2rem 6.4rem;
                        gap: 150px;
                        > div {
                          flex: 0 0 calc(50% - 75px);
                        }
                      }
                    }
                  `}
                >
                  <div>
                    <Typography
                      component={'h3'}
                      css={css`
                        line-height: 115%;
                        font-size: 3.2rem;
                        color: #000000;
                        margin-bottom: 3.2rem;

                        @media screen and (min-width: 576px) {
                          font-size: 3.6rem;
                        }
                        @media screen and (min-width: 1024px) {
                          font-size: 4.4rem;
                        }
                      `}
                    >
                      The reservation platform that works for the hospitality industry
                    </Typography>

                    <Button
                      css={css`
                        @media screen and (max-width: 576px) {
                          margin-bottom: 4rem;
                        }
                      `}
                      size="medium"
                      variant="primary"
                      href={href}
                      component={Anchor}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        amplitudeSendEvent(ACTION_NAVIGATE_TO_JOIN, {
                          pageWidgetListIndex: pageIndex,
                          widgetName: 'B2B Join',
                          widgetType: 'Marketing widget',
                        });
                      }}
                      iconEnd={<IconExternalLink />}
                    >
                      Use Tock at your business
                    </Button>
                  </div>
                  <span
                    css={({ borderRadius }: Theme) => css`
                      img {
                        width: 100%;
                        border-radius: ${borderRadius[200]};
                        display: block;
                      }
                    `}
                  >
                    {/* Mobile */}
                    {inView ? (
                      <picture
                        css={css`
                          @media screen and (min-width: 840px) {
                            display: none;
                          }
                        `}
                      >
                        <source
                          type="image/webp"
                          src="https://tock-public.imgix.net/marketing-widgets/b2b/marketing-join-t.webp"
                        />
                        <img
                          src="https://tock-public.imgix.net/marketing-widgets/b2b/marketing-join-t.jpg"
                          alt="Host managing Tock's Dashboard app on a tablet device"
                        />
                      </picture>
                    ) : null}

                    {/* Desktop */}

                    {inView ? (
                      <picture
                        css={css`
                          @media screen and (max-width: 840px) {
                            display: none;
                          }
                        `}
                      >
                        <source
                          srcSet="https://tock-public.imgix.net/marketing-widgets/b2b/marketing-join-d.webp"
                          type="image/webp"
                        />
                        <img
                          src="https://tock-public.imgix.net/marketing-widgets/b2b/marketing-join-d.jpg"
                          alt="Host managing Tock's Dashboard app on a tablet device"
                        />
                      </picture>
                    ) : null}
                  </span>
                </div>
              </div>
            )}
          </InView>
        );
      }
      case 'blog': {
        return (
          <InView threshold={0} rootMargin="200px 0px 200px 0px" triggerOnce>
            {({ inView, ref, entry }) => (
              <div className="MarketingWidget-content" ref={ref}>
                <div
                  className="MarketingWidget-messaging"
                  css={css`
                    padding: 3.2rem 2rem 0;
                    font-weight: 500;
                    @media screen and (min-width: 840px) {
                      padding: 3.2rem;
                    }
                  `}
                >
                  <h3
                    css={css`
                      font-size: 1.4rem;
                      line-height: 110%;
                      margin-bottom: 1.6rem;
                    `}
                  >
                    The Tock Blog
                  </h3>
                  <h4
                    css={css`
                      font-size: 2.8rem;
                      line-height: 110%;
                      max-width: 327px;
                      @media screen and (min-width: 600px) {
                        font-size: 3.2rem;
                      }
                    `}
                  >
                    Interviews and stories from the world of hospitality
                  </h4>
                  <Button
                    css={css`
                      margin-top: 2.4rem;
                    `}
                    variant="primary"
                    href="https://www.exploretock.com/blog"
                    size="large"
                    component={Anchor}
                    target="_blank"
                    onClick={() => {
                      amplitudeSendEvent(ACTION_NAVIGATE_TO_BLOG, {
                        pageWidgetListIndex: pageIndex,
                        widgetName: 'B2C Blog',
                        widgetType: 'Marketing widget',
                      });
                    }}
                  >
                    Read the latest
                  </Button>
                </div>

                <div
                  ref={ref}
                  className="MarketingWidget-blog-graphic"
                  css={css`
                    align-self: flex-end;
                  `}
                >
                  {/* Mobile and Tablet */}
                  <div
                    css={css`
                      display: flex;
                      gap: 2.6rem;
                      @media screen and (min-width: 840px) {
                        display: none;
                      }
                      picture {
                        width: 100%;
                        &:nth-of-type(1) {
                          flex: 0 0 55%;
                          align-self: flex-end;
                          img {
                            border-radius: 0 8px 0 0;
                          }
                        }
                        &:nth-of-type(2) {
                          margin-bottom: 4.8rem;
                          img {
                            border-radius: 8px 0 0 8px;
                          }
                        }
                        img {
                          width: 100%;
                          display: block;
                        }
                      }
                    `}
                  >
                    {inView ? (
                      <picture>
                        <source
                          srcSet="https://tock-public.imgix.net/marketing-widgets/blog/2024/blog-crafting-m.webp"
                          type="image/webp"
                        />
                        <img
                          src="https://tock-public.imgix.net/marketing-widgets/blog/2024/blog-crafting-m.png"
                          alt="Person pouring liquid into ceramic cup at a restaurant"
                        />
                      </picture>
                    ) : null}
                    {inView ? (
                      <picture>
                        <source
                          srcSet="https://tock-public.imgix.net/marketing-widgets/blog/2024/blog-dessert-m.webp"
                          type="image/webp"
                        />
                        <img
                          src="https://tock-public.imgix.net/marketing-widgets/blog/2024/blog-dessert-m.png"
                          alt="decadent colorful dessert"
                        />
                      </picture>
                    ) : null}
                  </div>

                  {/* Desktop 1 */}
                  <div
                    css={css`
                      @media screen and (max-width: 840px) {
                        display: none;
                      }
                      width: 100%;
                      height: 100%;
                      position: relative;
                      picture {
                        width: 100%;
                        position: absolute;

                        &:nth-of-type(1) {
                          top: 0;
                          right: 0;
                          max-width: 50%;
                          img {
                            width: 100%;
                            display: block;
                            border-radius: 0 0 0 8px;
                          }
                        }
                        &:nth-of-type(2) {
                          top: auto;
                          bottom: 0;
                          max-width: 75%;
                          @media screen and (max-width: 992px) {
                            right: 16px;
                          }
                          right: 32px;
                          @media screen and (min-width: 1440px) {
                            max-width: 70%;
                          }
                          img {
                            width: 100%;
                            display: block;
                            border-radius: 8px 8px 0 0;
                          }
                        }
                      }
                    `}
                  >
                    {inView ? (
                      <picture>
                        <source
                          srcSet="https://tock-public.imgix.net/marketing-widgets/blog/2024/blog-dessert-d.webp"
                          type="image/webp"
                        />
                        <img
                          src="https://tock-public.imgix.net/marketing-widgets/blog/2024/blog-dessert-d.png"
                          alt="decadent colorful dessert"
                        />
                      </picture>
                    ) : null}
                    {inView ? (
                      <picture>
                        <source
                          srcSet="https://tock-public.imgix.net/marketing-widgets/blog/2024/blog-crafting-d.webp"
                          type="image/webp"
                        />
                        <img
                          src="https://tock-public.imgix.net/marketing-widgets/blog/2024/blog-crafting-d.png"
                          alt="Person pouring liquid into ceramic cup at a restaurant"
                        />
                      </picture>
                    ) : null}
                  </div>
                </div>
              </div>
            )}
          </InView>
        );
      }
      case 'inYourCity': {
        return (
          <InView threshold={0} rootMargin="200px 0px 200px 0px" triggerOnce>
            {({ inView, ref, entry }) => (
              <div
                ref={ref}
                className="MarketingWidget-messaging"
                css={({ borderRadius }: Theme) => css`
                  background-color: #f4f4f4;
                  border-radius: ${borderRadius[200]};
                `}
              >
                <div
                  className="MarketingWidget-hero"
                  css={css`
                    padding: 2.8rem 1.6rem 1.6rem;
                    display: flex;
                    @media screen and (max-width: 575.9px) {
                      flex-direction: column;
                      > span {
                        order: 2;
                      }
                    }
                    @media screen and (min-width: 576px) {
                      gap: 44px;
                      justify-content: space-between;
                      align-items: center;
                      padding: 2.4rem;
                      > div {
                        flex: 0 0 50%;
                      }
                      @media screen and (min-width: 1024px) {
                        padding: 4.8rem;
                      }
                      @media screen and (min-width: 1280px) {
                        gap: 90px;
                        > div {
                          flex: 0 0 calc(50% - 45px);
                        }
                      }
                    }
                  `}
                >
                  <span
                    css={css`
                      img {
                        width: 100%;
                        display: block;
                      }
                    `}
                  >
                    {/* Mobile */}
                    {inView ? (
                      <picture
                        css={css`
                          @media screen and (min-width: 576px) {
                            display: none;
                          }
                        `}
                      >
                        <source
                          type="image/webp"
                          srcSet="https://tock-public.imgix.net/marketing-widgets/in-your-city/hp-localized-m.webp"
                        />
                        <img
                          src="https://tock-public.imgix.net/marketing-widgets/in-your-city/hp-localized-m.png"
                          alt="Tock Customers - Cocorico from Ashland, OR, Atoma from Seattle, WA, and Westerly Canteen from Wassaic, NY"
                        />
                      </picture>
                    ) : null}

                    {/* Desktop */}
                    {inView ? (
                      <picture
                        css={css`
                          @media screen and (max-width: 576px) {
                            display: none;
                          }
                          @media screen and (min-width: 1024px) {
                            display: none;
                          }
                        `}
                      >
                        <source
                          srcSet="https://tock-public.imgix.net/marketing-widgets/in-your-city/hp-localized-t.webp"
                          type="image/webp"
                        />
                        <img
                          src="https://tock-public.imgix.net/marketing-widgets/in-your-city/hp-localized-t.png"
                          alt="Tock Customers - Cocorico from Ashland, OR, Atoma from Seattle, WA, and Westerly Canteen from Wassaic, NY"
                        />
                      </picture>
                    ) : null}

                    {/* Desktop */}
                    {inView ? (
                      <picture
                        css={css`
                          @media screen and (max-width: 1024px) {
                            display: none;
                          }
                          @media screen and (min-width: 1440px) {
                            display: none;
                          }
                        `}
                      >
                        <source
                          srcSet="https://tock-public.imgix.net/marketing-widgets/in-your-city/hp-localized-d-2.webp"
                          type="image/webp"
                        />
                        <img
                          src="https://tock-public.imgix.net/marketing-widgets/in-your-city/hp-localized-d-2.png"
                          alt="Tock Customers - Cocorico from Ashland, OR, Atoma from Seattle, WA, and Westerly Canteen from Wassaic, NY"
                        />
                      </picture>
                    ) : null}
                    {/* XL Desktop */}
                    {inView ? (
                      <picture
                        css={css`
                          @media screen and (max-width: 1440px) {
                            display: none;
                          }
                        `}
                      >
                        <source
                          srcSet="https://tock-public.imgix.net/marketing-widgets/in-your-city/hp-localized-xl.webp"
                          type="image/webp"
                        />
                        <img
                          src="https://tock-public.imgix.net/marketing-widgets/in-your-city/hp-localized-xl.png"
                          alt="Tock Customers - Cocorico from Ashland, OR, Atoma from Seattle, WA, and Westerly Canteen from Wassaic, NY"
                        />
                      </picture>
                    ) : null}
                  </span>
                  <div>
                    <div
                      css={css`
                        line-height: 120%;
                        font-size: 1.6rem;
                        margin-bottom: 0.8rem;
                        @media screen and (min-width: 1024px) {
                          font-size: 2rem;
                          margin-bottom: 1.6rem;
                        }
                      `}
                    >
                      For businesses
                    </div>
                    <h3
                      css={css`
                        max-width: 420px;
                        line-height: 115%;
                        font-size: 3.4rem;
                        margin-bottom: 1.2rem;

                        @media screen and (min-width: 576px) {
                          font-size: 3.6rem;
                          margin-bottom: 2rem;
                        }
                        @media screen and (min-width: 1024px) {
                          font-size: 4.8rem;
                        }
                      `}
                    >
                      Tock powers hospitality all over the world.
                    </h3>

                    <div
                      css={css`
                        margin-bottom: 3.2rem;
                        line-height: 120%;
                        font-size: 2.2rem;
                        @media screen and (min-width: 576px) {
                          margin-bottom: 3.6rem;
                        }
                        @media screen and (min-width: 1024px) {
                          font-size: 2.4rem;
                        }
                      `}
                    >
                      See how we show up in{' '}
                      <br
                        css={css`
                          @media screen and (min-width: 576px) {
                            display: none;
                          }
                        `}
                      />
                      your city.
                    </div>
                    <Button
                      css={css`
                        @media screen and (max-width: 576px) {
                          margin-bottom: 4.4rem;
                        }
                      `}
                      className="MarketingWidget-button"
                      size="medium"
                      variant="primary"
                      href={`https://exploretock.com/join/in-your-city/${search}`}
                      component={Anchor}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        amplitudeSendEvent(ACTION_NAVIGATE_TO_JOIN, {
                          widgetName: 'B2B Join In Your City Marketing Widget',
                        });
                      }}
                      iconEnd={<IconExternalLink />}
                    >
                      Learn more
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </InView>
        );
      }
      case 'mobileApp': {
        return (
          <InView threshold={0} rootMargin="200px 0px 200px 0px" triggerOnce>
            {({ inView, ref, entry }) => (
              <div
                ref={ref}
                className="MarketingWidget-messaging"
                css={css`
                  width: 100%;
                  background-color: #000000;
                `}
              >
                <div
                  className="MarketingWidget-hero"
                  css={css`
                    .MarketingWidget-mobileImage,
                    .MarketingWidget-desktopImage {
                      position: relative;
                    }
                    align-items: unset !important;

                    @media screen and (min-width: 1450px) {
                      max-width: 1300px;
                      margin: 0 auto;
                    }
                    @media screen and (min-width: 840px) {
                      display: flex;
                      flex-direction: row !important;

                      > div:last-of-type {
                        flex: 0 0 55%;
                        justify-content: flex-end;
                      }
                      > div:first-of-type {
                        flex: 0 0 45%;
                      }
                    }
                  `}
                >
                  <div
                    css={css`
                      padding: 2.8rem 2.4rem 0;

                      @media screen and (min-width: 840px) {
                        text-align: left;
                        padding: 2.8rem 0 2.8rem 3.2rem;
                      }

                      @media screen and (min-width: 960px) {
                        width: 100%;
                      }
                    `}
                  >
                    <h3
                      css={css`
                        max-width: 211px;
                        font-size: 2.8rem;
                        line-height: 110%;
                        color: #fff;

                        @media screen and (min-width: 600px) {
                          font-size: 3.2rem;
                        }
                      `}
                    >
                      Get the Tock iOS app
                    </h3>

                    <div>
                      <p
                        className="MarketingWidget-paragraph"
                        css={css`
                          position: relative;
                          font-size: 1.4rem;
                          line-height: 130%;
                          color: #fff;
                          margin: 1.4rem 0 2.6rem;
                          @media screen and (min-width: 840px) {
                            margin: 1.4rem 0 3.6rem;
                          }
                        `}
                      >
                        Discover and book unique dining experiences all around the world.
                      </p>

                      <a
                        href="https://apps.apple.com/us/app/tock-restaurant-reservations/id1369347408"
                        aria-label="Download on the App Store"
                        target="_blank"
                        rel="noreferrer"
                        onClick={() =>
                          amplitudeSendEvent(ACTION_DOWNLOAD_IOS_APP, {
                            pageWidgetListIndex: pageIndex,
                            widgetName: 'mobile iOS',
                            widgetType: 'Marketing widget',
                          })
                        }
                      >
                        <img
                          css={css`
                            display: block;
                            max-width: 14.8rem;
                            @media screen and (min-width: 960px) {
                              max-width: 16.4rem;
                            }
                          `}
                          src="https://tock-public.imgix.net/marketing-widgets/mobile-app/Download_on_the_App_Store_Badge.svg"
                          alt=""
                        />
                      </a>
                    </div>
                  </div>
                  <div
                    css={css`
                      display: flex;
                      justify-content: flex-end;
                      align-self: flex-end;
                    `}
                  >
                    {/* Mobile */}
                    {inView ? (
                      <picture
                        className="MarketingWidget-mobileImage"
                        css={css`
                          max-width: 28.9rem;
                          img,
                          picture {
                            display: block;
                            height: auto;
                            width: 100%;
                          }
                          @media screen and (min-width: 840px) {
                            display: none;
                          }
                        `}
                      >
                        <source
                          type="image/webp"
                          srcSet="https://tock-public.imgix.net/marketing-widgets/mobile-app/ios-iphone-mobile.webp"
                        />
                        <img
                          src="https://tock-public.imgix.net/marketing-widgets/mobile-app/ios-iphone-mobile.png"
                          alt="iPhone Tock app"
                        />
                      </picture>
                    ) : null}

                    {/* Desktop */}
                    <div
                      css={css`
                        @media screen and (max-width: 840px) {
                          display: none;
                        }
                        width: 100%;
                        height: 100%;
                        position: relative;
                        max-width: 323px;
                        picture {
                          top: auto;
                          bottom: 0;
                        }
                        img {
                          display: block;
                          height: auto;
                          width: 100%;
                        }
                      `}
                    >
                      {inView ? (
                        <picture
                          css={css`
                            margin-left: 2.4rem;
                          `}
                        >
                          <source
                            srcSet="https://tock-public.imgix.net/marketing-widgets/mobile-app/ios-iphone-desktop.webp"
                            type="image/webp"
                          />
                          <img
                            src="https://tock-public.imgix.net/marketing-widgets/mobile-app/ios-iphone-desktop.png"
                            alt="iPhone Tock app"
                          />
                        </picture>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </InView>
        );
      }
      case 'tockTen': {
        return (
          <InView threshold={0} rootMargin="200px 0px 200px 0px" triggerOnce>
            {({ inView, ref, entry }) => (
              <div
                ref={ref}
                className="MarketingWidget-messaging"
                css={css`
                  width: 100%;
                  background-color: #ffdb8d;
                  padding: 2.4rem 2.4rem 3.2rem;
                  @media screen and (min-width: 768px) {
                    padding: 3.2rem 4.2rem;
                    display: flex;
                    gap: 7rem;
                    align-items: center;
                  }
                `}
              >
                <div
                  css={(theme: Theme) => css`
                    width: 100%;
                    height: 100%;
                    position: relative;
                    max-width: 230px;
                    ${theme.breakpoints.down('sm')} {
                      margin: 0 auto ${theme.spacing(4)}px;
                    }
                    ${theme.breakpoints.up('lg')} {
                      max-width: 323px;
                    }

                    img {
                      display: block;
                      height: auto;
                      width: 100%;
                    }
                  `}
                >
                  {inView ? (
                    <Link
                      underline="none"
                      href="https://www.exploretock.com/blog/tock-ten/"
                      target="_blank"
                      onClick={() => {
                        amplitudeSendEvent(ACTION_NAVIGATE_TO_BLOG, {
                          pageWidgetListIndex: pageIndex,
                          widgetName: 'B2C Blog - Tock Ten Collections Homepage (image)',
                          widgetType: 'Marketing widget',
                        });
                      }}
                    >
                      <picture>
                        <source
                          srcSet="https://tock-public.imgix.net/marketing-widgets/tock-ten/tock-10-all-posters.webp"
                          type="image/webp"
                        />
                        <img
                          src="https://tock-public.imgix.net/marketing-widgets/tock-ten/tock-10-all-posters.png"
                          alt="Tock 10 Posters - illustrations of posters from the Tock Ten collection with foreground poster of collage bowl and noodles with chopsticks"
                        />
                      </picture>
                    </Link>
                  ) : null}
                </div>
                <div
                  css={(theme: Theme) => css`
                    ${theme.breakpoints.up('lg')} {
                      margin: 0 auto;
                    }
                  `}
                >
                  <Typography
                    component="h3"
                    css={(theme: Theme) => css`
                      margin-bottom: ${theme.spacing(3)}px;
                      color: #000000;
                    `}
                  >
                    <TockLogo
                      variant="tock10"
                      css={css`
                        width: 72px;
                        height: 15px;
                      `}
                    />
                    <span
                      css={css`
                        opacity: 0;
                        visibility: hidden;
                        display: none;
                      `}
                    >
                      Tock 10
                    </span>
                  </Typography>
                  <Typography
                    paragraph
                    css={(theme: Theme) => css`
                      color: #000000;
                      font-size: 2rem;
                      line-height: 115%;
                      font-weight: 400;

                      ${theme.breakpoints.up('sm')} {
                        font-size: 2.4rem;
                        max-width: 500px;
                      }
                      ${theme.breakpoints.up('md')} {
                        font-size: 3.2rem;
                      }
                    `}
                  >
                    Celebrating the people, passion, and stories behind some of the most exciting
                    restaurants and wineries across the country.
                  </Typography>
                  <Link
                    css={(theme: Theme) => css`
                      margin-top: ${theme.spacing(3)}px;
                      color: #000000;
                    `}
                    iconEnd={<IconArrowRight />}
                    href="https://www.exploretock.com/blog/tock-ten/"
                    size="large"
                    target="_blank"
                    onClick={() => {
                      amplitudeSendEvent(ACTION_NAVIGATE_TO_BLOG, {
                        pageWidgetListIndex: pageIndex,
                        widgetName: 'B2C Blog - Tock Ten Collections Homepage (link)',
                        widgetType: 'Marketing widget',
                      });
                    }}
                  >
                    View the collections
                  </Link>
                </div>
              </div>
            )}
          </InView>
        );
      }
      case 'tockTenCollection': {
        return (
          <InView threshold={0} rootMargin="200px 0px 200px 0px" triggerOnce>
            {({ inView, ref, entry }) => (
              <div
                ref={ref}
                className="MarketingWidget-messaging"
                css={css`
                  width: 100%;
                  background-color: #ffd4ed;
                  padding: 2.4rem 2.4rem 3.2rem;
                  @media screen and (min-width: 768px) {
                    padding: 3.2rem 4.2rem;
                    display: flex;
                    gap: 7rem;
                    align-items: center;
                  }
                `}
              >
                <div
                  css={(theme: Theme) => css`
                    width: 100%;
                    height: 100%;
                    position: relative;
                    ${theme.breakpoints.down('sm')} {
                      margin: 0 auto ${theme.spacing(4)}px;
                      max-width: 100%;
                    }
                    ${theme.breakpoints.up('sm')} {
                      max-width: 230px;
                    }
                    ${theme.breakpoints.up('lg')} {
                      max-width: 323px;
                    }

                    img {
                      display: block;
                      height: auto;
                      width: 100%;
                    }
                  `}
                >
                  {inView ? (
                    <Link
                      underline="none"
                      href="https://www.exploretock.com/blog/tock-ten/open-fire/"
                      target="_blank"
                      onClick={() => {
                        amplitudeSendEvent(ACTION_NAVIGATE_TO_BLOG, {
                          pageWidgetListIndex: pageIndex,
                          widgetName: 'B2C Blog - Tock Ten - Open Fire (image)',
                          widgetType: 'Marketing widget',
                        });
                      }}
                    >
                      <picture>
                        <source
                          srcSet="https://tock-public.imgix.net/marketing-widgets/tock-ten/open-fire.webp"
                          type="image/webp"
                        />
                        <img
                          src="https://tock-public.imgix.net/marketing-widgets/tock-ten/open-fire.jpg"
                          alt="Open Fire - Tock 10 Poster - abstract illustration of a wood-fired grill with skewered meats and vegetables roasting over embers"
                        />
                      </picture>
                    </Link>
                  ) : null}
                </div>
                <div
                  css={(theme: Theme) => css`
                    ${theme.breakpoints.up('lg')} {
                      margin: 0 auto;
                    }
                  `}
                >
                  <Typography
                    component="h3"
                    css={(theme: Theme) => css`
                      margin-bottom: ${theme.spacing(3)}px;
                      color: #470000;
                    `}
                  >
                    <TockLogo
                      variant="tock10"
                      css={css`
                        width: 72px;
                        height: 15px;
                      `}
                    />
                    <span
                      css={css`
                        opacity: 0;
                        visibility: hidden;
                        display: none;
                      `}
                    >
                      Tock 10
                    </span>
                  </Typography>
                  <Typography
                    paragraph
                    css={(theme: Theme) => css`
                      color: #470000;
                      font-size: 2rem;
                      line-height: 115%;
                      font-weight: 400;

                      ${theme.breakpoints.up('sm')} {
                        font-size: 2.4rem;
                        max-width: 500px;
                      }
                      ${theme.breakpoints.up('md')} {
                        font-size: 3.2rem;
                      }
                    `}
                  >
                    Yakitori. Wood-burning. Ember-roasting. These are the restaurants that harness
                    the primal power of fire.
                  </Typography>
                  <Link
                    css={(theme: Theme) => css`
                      margin-top: ${theme.spacing(3)}px;
                      color: #470000;
                    `}
                    iconEnd={<IconArrowRight />}
                    href="https://www.exploretock.com/blog/tock-ten/open-fire/"
                    size="large"
                    target="_blank"
                    onClick={() => {
                      amplitudeSendEvent(ACTION_NAVIGATE_TO_BLOG, {
                        pageWidgetListIndex: pageIndex,
                        widgetName: 'B2C Blog - Tock Ten - Open Fire (link)',
                        widgetType: 'Marketing widget',
                      });
                    }}
                  >
                    View the collection
                  </Link>
                </div>
              </div>
            )}
          </InView>
        );
      }

      default: {
        return (
          <div className="MarketingWidget-content">
            <div className="MarketingWidget-messaging">
              <Typography component="span" variant="caption" color="fgWarningBase">
                Invalid marketing widget
              </Typography>
            </div>
          </div>
        );
      }
    }
  };

  const renderColumn = (column: { value: string }, pageIndex: number, search: string) => {
    if (!column || !column.value) {
      return null;
    }

    return (
      <div className={`MarketingWidget-column MarketingWidget-${column.value}`}>
        {renderWidget(column.value, { pageIndex }, search)}
      </div>
    );
  };

  const amplitudeSendEvent = useAmplitudeSendEvent();
  const { search } = useLocation();
  const widgetModifiers = widget.widgetModifierFields;
  const contentColumnA = _.find(widgetModifiers, { key: Modifiers.contentColumnA });
  const contentColumnB = _.find(widgetModifiers, { key: Modifiers.contentColumnB });

  if (!contentColumnA && !contentColumnB) {
    return null;
  }

  return (
    <WidgetLayoutContainer css={MarketingWidgetStyles}>
      <div className="MarketingWidget-columns">
        {contentColumnA && renderColumn(contentColumnA, pageIndex, search)}
        {contentColumnB && renderColumn(contentColumnB, pageIndex, search)}
      </div>
    </WidgetLayoutContainer>
  );
};

export default MarketingWidget;
